import { useState, useEffect, useRef } from "react";

const steps = [
  { id: 1, title: "Problem Identification", color: "from-red-500 to-red-600" },
  { id: 2, title: "Establish Objectives", color: "from-yellow-500 to-yellow-600" },
  { id: 3, title: "Research Implementation", color: "from-green-400 to-green-600" },
  { id: 4, title: "Result Analysis", color: "from-purple-500 to-purple-600" },
  { id: 5, title: "Publications", color: "from-blue-500 to-blue-600" },
];

const circles = [
  { id: 1, title: "Define Problem", color: "from-red-400 to-red-600" },
  { id: 2, title: "Design Research", color: "from-yellow-400 to-yellow-600" },
  { id: 3, title: "Data Analysis", color: "from-green-400 to-green-600" },
  { id: 4, title: "Report Validation", color: "from-purple-400 to-purple-600" },
  { id: 5, title: "Journals", color: "from-blue-400 to-blue-600" },
];

export default function FlowChart() {
  const [currentStep, setCurrentStep] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const restartTimer = useRef(null);
  const countdownTimer = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Main animation logic
  useEffect(() => {
    if (animationComplete) return;

    const timer = setInterval(() => {
      setCurrentStep((prev) => {
        const maxSteps = isMobile ? 4 : 9; // Mobile: 0-4 (5 steps), Desktop: 0-9 (10 steps)
        if (prev >= maxSteps) {
          setAnimationComplete(true);
          clearInterval(timer);
          return maxSteps;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(timer);
  }, [animationComplete, isMobile]);

  // Auto-restart logic with countdown
  useEffect(() => {
    if (animationComplete) {
      // Start 3-second countdown
      setCountdown(3);

      countdownTimer.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Restart after 3 seconds
      restartTimer.current = setTimeout(() => {
        setCurrentStep(0);
        setAnimationComplete(false);
        setCountdown(0);
      }, 3000);
    }

    return () => {
      if (restartTimer.current) clearTimeout(restartTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, [animationComplete]);

  // Desktop step visibility logic
  const getDesktopStepVisibility = (stepIndex) => {
    const sequenceMap = {
      'circle-1': 0, 'top-1': 1,
      'circle-2': 2, 'bottom-2': 3,
      'circle-3': 4, 'top-3': 5,
      'circle-4': 6, 'bottom-4': 7,
      'circle-5': 8, 'top-5': 9
    };
    
    return currentStep >= (sequenceMap[stepIndex] || 0);
  };

  // Mobile step visibility logic - simpler sequential animation
  const getMobileStepVisibility = (stepIndex) => {
    return currentStep >= stepIndex;
  };

  const getConnectorVisibility = (connectorIndex) => {
    if (isMobile) return true; // Always show connectors on mobile
    const connectorSteps = [1, 3, 5, 7];
    return currentStep >= (connectorSteps[connectorIndex] || 0);
  };




  const getCurrentStepText = () => {
    if (isMobile) {
      if (!animationComplete) {
        return `Step ${currentStep + 1}: ${steps[currentStep]?.title || 'Getting Started...'}`;
      }
    } else {
      if (!animationComplete) {
        const stepTexts = [
          'Step 1: Define Problem',
          'Step 1: Problem Identification',
          'Step 2: Design Research', 
          'Step 2: Establish Objectives',
          'Step 3: Data Analysis',
          'Step 3: Research Implementation',
          'Step 4: Report Validation',
          'Step 4: Result Analysis',
          'Step 5: Journals',
          'Step 5: Publications'
        ];
        return stepTexts[currentStep] || 'Getting Started...';
      }
    }
    
    if (countdown > 0) {
      return `Restarting in ${countdown}...`;
    }
    return 'Process Complete!';
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4 md:p-8">
      {/* Header */}
      <div className="mb-8 md:mb-16 text-center">
        <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">Research Process Roadmap</h1>
        <p className="text-sm md:text-lg text-gray-600">Sequential methodology for systematic research</p>
      </div>

      {/* Desktop Layout (hidden on mobile) */}
      <div className="hidden md:block relative w-full max-w-6xl">
        {/* Main Flow Container */}
        <div className="relative">
          
          {/* Top Row - Steps 1, 3, 5 positioned to align with circles */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center" style={{ width: '100%', maxWidth: '800px' }}>
              {/* Step 1 - Problem Identification (aligns with circle 1) */}
              <div className="flex flex-col items-center" style={{ width: '20%' }}>
                <div className={`
                  bg-gradient-to-r ${steps[0].color} text-white px-4 py-3 rounded-xl shadow-lg text-center
                  transform transition-all duration-600 ease-out hover:scale-105
                  ${getDesktopStepVisibility('top-1') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
                `}>
                  <div className="flex items-center justify-center space-x-1">
                    <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center font-bold text-xs">
                      {steps[0].id}
                    </div>
                    <h3 className="font-bold text-xs">{steps[0].title}</h3>
                  </div>
                </div>
                {/* Connecting Line Down */}
                <div className={`w-1 h-8 bg-gradient-to-b from-red-400 to-red-600 mt-2 transition-all duration-500 ease-in-out ${
                  getDesktopStepVisibility('top-1') ? 'opacity-100' : 'opacity-0'
                }`}></div>
              </div>

              {/* Spacer to align with circle 2 position */}
              <div style={{ width: '20%' }}></div>

              {/* Step 3 - Research Implementation (aligns with circle 3) */}
              <div className="flex flex-col items-center" style={{ width: '20%' }}>
                <div className={`
                  bg-gradient-to-r ${steps[2].color} text-white px-4 py-3 rounded-xl shadow-lg text-center
                  transform transition-all duration-600 ease-out hover:scale-105
                  ${getDesktopStepVisibility('top-3') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
                `}>
                  <div className="flex items-center justify-center space-x-1">
                    <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center font-bold text-xs">
                      {steps[2].id}
                    </div>
                    <h3 className="font-bold text-xs">{steps[2].title}</h3>
                  </div>
                </div>
                {/* Connecting Line Down */}
                <div className={`w-1 h-8 bg-gradient-to-b from-green-400 to-green-600 mt-2 transition-all duration-500 ease-in-out ${
                  getDesktopStepVisibility('top-3') ? 'opacity-100' : 'opacity-0'
                }`}></div>
              </div>

              {/* Spacer to align with circle 4 position */}
              <div style={{ width: '20%' }}></div>

              {/* Step 5 - Publications (aligns with circle 5) */}
              <div className="flex flex-col items-center" style={{ width: '20%' }}>
                <div className={`
                  bg-gradient-to-r ${steps[4].color} text-white px-4 py-3 rounded-xl shadow-lg text-center
                  transform transition-all duration-600 ease-out hover:scale-105
                  ${getDesktopStepVisibility('top-5') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
                `}>
                  <div className="flex items-center justify-center space-x-1">
                    <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center font-bold text-xs">
                      {steps[4].id}
                    </div>
                    <h3 className="font-bold text-xs">{steps[4].title}</h3>
                  </div>
                </div>
                {/* Connecting Line Down */}
                <div className={`w-1 h-8 bg-gradient-to-b from-blue-400 to-blue-600 mt-2 transition-all duration-500 ease-in-out ${
                  getDesktopStepVisibility('top-5') ? 'opacity-100' : 'opacity-0'
                }`}></div>
              </div>
            </div>
          </div>

          {/* Middle Row - Circles with Original Overlapping Connectors */}
          <div className="flex justify-center items-center mb-8">
            {circles.map((circle, index) => (
              <div key={circle.id} className="flex items-center">
                {/* Circle */}
                <div className={`
                  relative w-28 h-28 rounded-full bg-gradient-to-br ${circle.color} 
                  shadow-xl flex items-center justify-center text-white font-bold text-center
                  transform transition-all duration-600 ease-out z-10
                  ${getDesktopStepVisibility(`circle-${circle.id}`) ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}
                `}>
                  <div className="text-xs leading-tight px-2">{circle.title}</div>
                  
                  {/* Glowing effect for current active circle */}
                  {getDesktopStepVisibility(`circle-${circle.id}`) && currentStep % 2 === 0 && Math.floor(currentStep / 2) === index && (
                    <div className="absolute inset-0 rounded-full bg-white opacity-30 animate-pulse"></div>
                  )}
                </div>

                {/* Overlapping Ring Connector (except for last circle) */}
                {index < circles.length - 1 && (
                  <div className="relative -mx-4 z-0">
                    {/* Outer Ring Connector */}
                    <div className={`
                      w-20 h-20 rounded-full border-4 transform transition-all duration-500 ease-in-out
                      ${getConnectorVisibility(index) ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
                    `}
                    style={{
                      borderColor: getConnectorVisibility(index) ? 
                        (index === 0 ? '#ef4444' :
                         index === 1 ? '#eab308' :
                         index === 2 ? '#14b8a6' :
                         index === 3 ? '#a855f7' : '#3b82f6') : '#d1d5db'
                    }}>
                      
                      {/* Inner connector circle */}
                      <div className="w-full h-full rounded-full bg-white shadow-inner flex items-center justify-center">
                        {/* Flow indicator dot */}
                        <div className={`
                          w-2 h-2 rounded-full transition-all duration-500 ease-in-out
                          ${getConnectorVisibility(index) ? 'bg-gradient-to-r from-indigo-500 to-purple-600 animate-pulse' : 'bg-gray-400'}
                        `}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom Row - Steps 2, 4 positioned to align with circles 2 and 4 */}
          <div className="flex justify-center">
            <div className="flex items-center" style={{ width: '100%', maxWidth: '800px' }}>
              {/* Spacer to align with circle 1 position */}
              <div style={{ width: '20%' }}></div>

              {/* Step 2 - Establish Objectives (aligns with circle 2) */}
              <div className="flex flex-col items-center" style={{ width: '20%' }}>
                <div className={`w-1 h-8 bg-gradient-to-b from-yellow-400 to-yellow-600 mb-2 transition-all duration-400 ease-in-out ${
                  getDesktopStepVisibility('bottom-2') ? 'opacity-100' : 'opacity-0'
                }`}></div>
                <div className={`
                  bg-gradient-to-r ${steps[1].color} text-white px-4 py-3 rounded-xl shadow-lg text-center
                  transform transition-all duration-600 ease-out hover:scale-105
                  ${getDesktopStepVisibility('bottom-2') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
                `}>
                  <div className="flex items-center justify-center space-x-1">
                    <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center font-bold text-xs">
                      {steps[1].id}
                    </div>
                    <h3 className="font-bold text-xs">{steps[1].title}</h3>
                  </div>
                </div>
              </div>

              {/* Spacer to align with circle 3 position */}
              <div style={{ width: '20%' }}></div>

              {/* Step 4 - Result Analysis (aligns with circle 4) */}
              <div className="flex flex-col items-center" style={{ width: '20%' }}>
                <div className={`w-1 h-8 bg-gradient-to-b from-purple-400 to-purple-600 mb-2 transition-all duration-400 ease-in-out ${
                  getDesktopStepVisibility('bottom-4') ? 'opacity-100' : 'opacity-0'
                }`}></div>
                <div className={`
                  bg-gradient-to-r ${steps[3].color} text-white px-4 py-3 rounded-xl shadow-lg text-center
                  transform transition-all duration-600 ease-out hover:scale-105
                  ${getDesktopStepVisibility('bottom-4') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}
                `}>
                  <div className="flex items-center justify-center space-x-1">
                    <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center font-bold text-xs">
                      {steps[3].id}
                    </div>
                    <h3 className="font-bold text-xs">{steps[3].title}</h3>
                  </div>
                </div>
              </div>

              {/* Spacer to align with circle 5 position */}
              <div style={{ width: '20%' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout (shown only on mobile) */}
      <div className="md:hidden w-full max-w-sm">
        <div className="relative">
          {/* Vertical connector line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-red-400 via-yellow-400 via-green-400 via-purple-400 to-blue-400 z-0"></div>
          
          {/* Steps in vertical order */}
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-start mb-6 relative">
              {/* Circle */}
              <div className={`
                relative w-12 h-12 rounded-full bg-gradient-to-br ${circles[index].color} 
                shadow-lg flex items-center justify-center text-white font-bold text-center z-10 flex-shrink-0
                transform transition-all duration-600 ease-out
                ${getMobileStepVisibility(index) ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}
              `}>
                <div className="text-xs leading-tight">{step.id}</div>
                
                {/* Glowing effect for current active step */}
                {getMobileStepVisibility(index) && currentStep === index && !animationComplete && (
                  <div className="absolute inset-0 rounded-full bg-white opacity-30 animate-pulse"></div>
                )}
              </div>

              {/* Step content */}
              <div className={`
                ml-4 flex-1 transform transition-all duration-600 ease-out
                ${getMobileStepVisibility(index) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
              `}>
                {/* Circle title */}
                <div className={`
                  bg-gradient-to-r ${circles[index].color} text-white px-3 py-1.5 rounded-lg shadow-md mb-2 text-sm font-semibold
                `}>
                  {circles[index].title}
                </div>
                
                {/* Step box */}
                <div className={`
                  bg-gradient-to-r ${step.color} text-white px-3 py-2 rounded-lg shadow-lg
                `}>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-white bg-opacity-20 rounded-full flex items-center justify-center font-bold text-xs">
                      {step.id}
                    </div>
                    <h3 className="font-bold text-sm">{step.title}</h3>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Current Step Indicator and Controls */}
      <div className="text-center mt-8 max-w-md">
        <div className="bg-white rounded-full px-4 py-2 shadow-lg inline-block mb-4">
          <span className="text-sm md:text-base text-gray-600 font-medium">
            {getCurrentStepText()}
          </span>
        </div>
        
        {/* Manual Restart Button */}
       
      </div>
    </div>
  );
}